import React, { useState } from "react";
import { BlockMath } from "react-katex";
import { CheckCircle2, XCircle, ChevronDown, AlertTriangle, Target, ListChecks, Lightbulb } from "lucide-react";
import { Rich, Paragraphs } from "./Rich";
import { ExplainSimpler } from "./ExplainSimpler";

const Label = ({ children, color }) => (
  <div className={`text-[11px] uppercase tracking-[0.18em] font-semibold mb-2 ${color}`}>{children}</div>
);

const Shell = ({ b, accent, children, explain, chapterId }) => (
  <div className={`rounded-xl border p-5 ${accent}`} data-testid={`note-block-${b.id}`} data-block-type={b.type}>
    {children}
    {explain && <ExplainSimpler chapterId={chapterId} blockId={b.id} />}
  </div>
);

const Overview = ({ b }) => (
  <div className="text-zinc-300 text-[15px]" data-testid={`note-block-${b.id}`}><Paragraphs items={b.body} /></div>
);

const Prerequisites = ({ b }) => (
  <Shell b={b} accent="border-sky-300/20 bg-sky-300/[0.04]">
    <Label color="text-sky-300">Before you start, you should know</Label>
    <ul className="space-y-1.5 text-sm text-zinc-300">
      {b.items.map((it, i) => (
        <li key={i} className="flex gap-2"><CheckCircle2 className="w-4 h-4 mt-0.5 text-sky-300 shrink-0" /><Rich>{it}</Rich></li>
      ))}
    </ul>
  </Shell>
);

const Concept = ({ b, chapterId }) => (
  <div className="border-l-2 border-cyan-400/40 pl-5" data-testid={`note-block-${b.id}`} data-block-type="concept">
    <h3 className="font-display text-lg font-semibold text-zinc-100 mb-2"><Rich>{b.title}</Rich></h3>
    <Paragraphs items={b.body} className="text-[15px] text-zinc-300" />
    <ExplainSimpler chapterId={chapterId} blockId={b.id} />
  </div>
);

const Formula = ({ b, chapterId }) => (
  <Shell b={b} accent="border-yellow-300/30 bg-yellow-300/[0.05]" explain chapterId={chapterId}>
    <Label color="text-yellow-300">Formula · <Rich>{b.title}</Rich></Label>
    <div className="overflow-x-auto py-1 text-zinc-50 text-lg"><BlockMath math={b.latex} renderError={() => <code>{b.latex}</code>} /></div>
    <p className="text-sm text-zinc-300 mt-2"><Rich>{b.explanation}</Rich></p>
    {b.when_to_use && <p className="text-xs text-zinc-400 mt-2"><span className="text-yellow-300/80">When to use: </span><Rich>{b.when_to_use}</Rich></p>}
  </Shell>
);

const Theorem = ({ b, chapterId, summary }) => {
  const [open, setOpen] = useState(false);
  return (
    <Shell b={b} accent="border-violet-300/25 bg-violet-300/[0.04]" explain={!summary} chapterId={chapterId}>
      <Label color="text-violet-300">Theorem · <Rich>{b.title}</Rich></Label>
      <p className="text-[15px] text-zinc-100 italic"><Rich>{b.statement}</Rich></p>
      {!summary && b.proof?.length > 0 && (
        <>
          <button onClick={() => setOpen(!open)} className="mt-3 text-xs text-violet-300 inline-flex items-center gap-1" data-testid={`proof-toggle-${b.id}`}>
            {open ? "Hide proof" : "Show proof"} <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
          </button>
          {open && (
            <ol className="mt-3 space-y-2 text-sm text-zinc-300 list-decimal pl-5 fade-up">
              {b.proof.map((s, i) => <li key={i}><Rich>{s}</Rich></li>)}
            </ol>
          )}
        </>
      )}
    </Shell>
  );
};

const DIFF = { easy: "text-emerald-300 border-emerald-300/30", medium: "text-sky-300 border-sky-300/30", hard: "text-pink-300 border-pink-300/30" };

const Example = ({ b, chapterId }) => {
  const [open, setOpen] = useState(false);
  return (
    <Shell b={b} accent="border-white/10 bg-[#152040]" explain={open} chapterId={chapterId}>
      <div className="flex items-center gap-2 mb-2">
        <Label color="text-zinc-400">Example{b.title ? <> · <Rich>{b.title}</Rich></> : null}</Label>
        <span className={`ml-auto -mt-2 text-[10px] uppercase tracking-wider border rounded-full px-2 py-0.5 ${DIFF[b.difficulty]}`}>{b.difficulty}</span>
      </div>
      <p className="text-[15px] text-zinc-100"><Rich>{b.question}</Rich></p>
      <button onClick={() => setOpen(!open)} className="mt-3 text-xs text-cyan-300 inline-flex items-center gap-1" data-testid={`example-toggle-${b.id}`}>
        {open ? "Hide solution" : "Try it, then show solution"} <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-3 fade-up">
          <ol className="space-y-2 text-sm text-zinc-300 list-decimal pl-5">{b.steps.map((s, i) => <li key={i}><Rich>{s}</Rich></li>)}</ol>
          <div className="mt-3 text-sm rounded-md bg-emerald-400/10 border border-emerald-400/25 px-3 py-2 text-emerald-200" data-testid={`example-answer-${b.id}`}>
            <span className="font-semibold">Answer: </span><Rich>{b.answer}</Rich>
          </div>
        </div>
      )}
    </Shell>
  );
};

const Mistake = ({ b, chapterId }) => (
  <Shell b={b} accent="border-red-400/25 bg-red-400/[0.04]" explain chapterId={chapterId}>
    <Label color="text-red-300"><span className="inline-flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" /> Common mistake</span></Label>
    <div className="grid sm:grid-cols-2 gap-3 text-sm">
      <div className="flex gap-2 text-zinc-300"><XCircle className="w-4 h-4 mt-0.5 text-red-400 shrink-0" /><Rich>{b.wrong}</Rich></div>
      <div className="flex gap-2 text-zinc-200"><CheckCircle2 className="w-4 h-4 mt-0.5 text-emerald-400 shrink-0" /><Rich>{b.right}</Rich></div>
    </div>
    <p className="text-xs text-zinc-400 mt-3"><span className="text-red-300/80">Why: </span><Rich>{b.why}</Rich></p>
  </Shell>
);

const Revision = ({ b }) => (
  <Shell b={b} accent="border-emerald-300/25 bg-emerald-300/[0.04]">
    <Label color="text-emerald-300"><span className="inline-flex items-center gap-1.5"><ListChecks className="w-3.5 h-3.5" /> Quick revision</span></Label>
    <ul className="space-y-2 text-sm text-zinc-200">
      {b.items.map((it, i) => <li key={i} className="flex gap-2"><span className="font-mono text-emerald-300/70 text-xs mt-0.5">{String(i + 1).padStart(2, "0")}</span><Rich>{it}</Rich></li>)}
    </ul>
  </Shell>
);

const ExamFocus = ({ b }) => (
  <Shell b={b} accent="border-pink-300/25 bg-pink-300/[0.04]">
    <Label color="text-pink-300"><span className="inline-flex items-center gap-1.5"><Target className="w-3.5 h-3.5" /> Exam focus</span></Label>
    <Paragraphs items={b.body} className="text-sm text-zinc-300 mb-3" />
    <ul className="space-y-1.5 text-sm text-zinc-200 list-disc pl-5">{b.items.map((it, i) => <li key={i}><Rich>{it}</Rich></li>)}</ul>
  </Shell>
);

const CheckQuestion = ({ b }) => {
  const [picked, setPicked] = useState(null);
  const [shown, setShown] = useState(false);
  const reveal = shown || picked !== null;
  return (
    <Shell b={b} accent="border-cyan-300/25 bg-cyan-300/[0.04]">
      <Label color="text-cyan-300"><span className="inline-flex items-center gap-1.5"><Lightbulb className="w-3.5 h-3.5" /> Check yourself</span></Label>
      <p className="text-[15px] text-zinc-100 mb-3"><Rich>{b.question}</Rich></p>
      {b.options?.length > 0 ? (
        <div className="grid sm:grid-cols-2 gap-2">
          {b.options.map((o, i) => {
            const correct = o === b.answer;
            const style = picked === null ? "border-white/10 hover:border-cyan-300/50" : correct ? "border-emerald-400/60 bg-emerald-400/10" : picked === i ? "border-red-400/60 bg-red-400/10" : "border-white/10 opacity-60";
            return (
              <button key={i} disabled={picked !== null} onClick={() => setPicked(i)} className={`text-left text-sm rounded-lg border px-3 py-2 text-zinc-200 transition-colors ${style}`} data-testid={`check-option-${b.id}-${i}`}>
                <Rich>{o}</Rich>
              </button>
            );
          })}
        </div>
      ) : (
        !shown && <button onClick={() => setShown(true)} className="text-xs text-cyan-300" data-testid={`check-reveal-${b.id}`}>Show answer</button>
      )}
      {reveal && (
        <div className="mt-3 text-sm text-zinc-300 fade-up" data-testid={`check-explanation-${b.id}`}>
          {picked !== null && <div className={`font-semibold mb-1 ${b.options[picked] === b.answer ? "text-emerald-300" : "text-red-300"}`}>{b.options[picked] === b.answer ? "Correct!" : "Not quite."}</div>}
          {!b.options?.length && <div className="mb-1"><span className="text-cyan-300">Answer: </span><Rich>{b.answer}</Rich></div>}
          <Rich>{b.explanation}</Rich>
        </div>
      )}
    </Shell>
  );
};

const Visual = ({ b }) => (
  <figure className="rounded-xl border border-white/10 bg-[#0b1428] p-4" data-testid={`note-block-${b.id}`} data-block-type="visual">
    <div className="text-amber-50 max-w-md mx-auto [&_svg]:w-full [&_svg]:h-auto" role="img" aria-label={b.alt} dangerouslySetInnerHTML={{ __html: b.svg }} />
    <figcaption className="text-xs text-zinc-400 text-center mt-3"><Rich>{b.caption}</Rich></figcaption>
  </figure>
);

const RENDERERS = {
  overview: Overview, prerequisites: Prerequisites, concept: Concept, formula: Formula, theorem: Theorem,
  example: Example, mistake: Mistake, revision: Revision, exam_focus: ExamFocus, check_question: CheckQuestion, visual: Visual,
};

export const SUMMARY_TYPES = new Set(["overview", "prerequisites", "formula", "theorem", "revision", "exam_focus"]);

export const NoteBlock = ({ block, chapterId, summary }) => {
  const R = RENDERERS[block.type];
  return R ? <R b={block} chapterId={chapterId} summary={summary} /> : null;
};
